import type { MapsLocale } from "../MapsLocale";
import { formatDateTimeAndUseOnlyParts } from "./formatDateTimeAndUseOnlyParts";
import type { DateTimeFormats } from "./types";

type FormatterCacheKey = `${DateTimeFormats}_${string}`;
const formatterCache = new Map<FormatterCacheKey, Intl.DateTimeFormat>();

export type FormatDateTimeFn = (
    value: Date | number,
    format: DateTimeFormats,
) => string;

export const formatDateTime = (
    value: Date | number,
    format: DateTimeFormats,
    locale: MapsLocale,
): string => {
    const formatOptions: Intl.DateTimeFormatOptions = {
        // @ts-expect-error TODO: why is locale.hourCycles not available in typescript types? it's valid according to MDN
        // see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/hourCycles
        // locale.hourCycle is undefined if not explicitly set, so we have to fallback to hourCycles[0]
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        hourCycle: locale.hourCycle ?? locale.hourCycles?.[0] ?? "h23",
    };

    switch (format) {
        case "hourAndMinuteWithOptionalDayPeriod":

        case "hourAndMinute": {
            formatOptions.hour =
                locale.language === "de" ? "2-digit" : "numeric";
            formatOptions.minute = "2-digit";
            break;
        }

        case "hourMinuteAndSecond": {
            formatOptions.hour = "2-digit";
            formatOptions.minute = "2-digit";
            formatOptions.second = "2-digit";
            break;
        }

        case "dateAndTime": {
            formatOptions.year = "2-digit";
            formatOptions.month = "2-digit";
            formatOptions.day = "2-digit";

            formatOptions.hour = "2-digit";
            formatOptions.minute = "2-digit";
            formatOptions.second = "2-digit";
            break;
        }

        case "weekdayLong": {
            formatOptions.weekday = "long";
            break;
        }

        case "monthLong": {
            formatOptions.month = "long";
            break;
        }

        case "hourWithOptionalDayPeriod":

        case "hour": {
            formatOptions.hour = "numeric";

            break;
        }
    }

    const formatterCacheKey: FormatterCacheKey = `${format}_${locale.languageAndRegion}`;

    // cache the formatter because its creation is kinda expensive performance wise
    if (!formatterCache.has(formatterCacheKey)) {
        formatterCache.set(
            formatterCacheKey,
            new Intl.DateTimeFormat(locale, formatOptions),
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const formatter = formatterCache.get(formatterCacheKey)!;

    switch (format) {
        case "hourAndMinute": {
            return formatDateTimeAndUseOnlyParts(
                formatter,
                value,
                (part) =>
                    part.type === "hour" ||
                    part.type === "minute" ||
                    part.type === "literal",
            );
        }

        case "hourAndMinuteWithOptionalDayPeriod": {
            return formatDateTimeAndUseOnlyParts(
                formatter,
                value,
                (part) =>
                    part.type === "hour" ||
                    part.type === "minute" ||
                    part.type === "literal" ||
                    part.type === "dayPeriod",
            );
        }

        case "hour": {
            return formatDateTimeAndUseOnlyParts(
                formatter,
                value,
                (part) => part.type === "hour",
            );
        }

        case "hourWithOptionalDayPeriod": {
            return formatDateTimeAndUseOnlyParts(
                formatter,
                value,
                (part) =>
                    part.type === "hour" ||
                    (part.type === "literal" && part.value === " ") ||
                    part.type === "dayPeriod",
            );
        }

        case "dateAndTime":
        case "hourMinuteAndSecond":
        case "weekdayLong":
        case "monthLong":
            return formatter.format(value);
    }
};
